import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Hash, History, Merge, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { TagPill } from "@/components/tag-chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { TagTrendMetricsCard } from "@/components/tag-trend-metrics-card";
import { invalidateTagTrendCacheFn } from "@/lib/tag-trend.functions";
import { useAuth } from "@/lib/auth-context";
import {
  fetchAdminTags,
  fetchTagAliases,
  addTagAlias,
  fetchTagAuditActors,
  fetchTagAuditLog,
  tagAuditToCsv,
  TAG_AUDIT_LABELS,
  normaliseTagQuery,
  searchTags,
  TAG_GROUP_LABELS,
  type AdminTagFilter,
} from "@/lib/tags";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/tags")({
  head: () => ({
    meta: [
      { title: "Tag management — Quibblies admin" },
      {
        name: "description",
        content:
          "Administrator tools for the Quibblies hashtag registry: approve requests, merge duplicates, create aliases, archive and block tags.",
      },
      { property: "og:title", content: "Tag management — Quibblies admin" },
      { property: "og:description", content: "Manage the Quibblies hashtag registry." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AdminTagsPage,
});

const FILTERS: { value: AdminTagFilter; label: string }[] = [
  { value: "most_used", label: "Most used" },
  { value: "trending", label: "Trending" },
  { value: "requests", label: "New requests" },
  { value: "recent", label: "Recently added" },
  { value: "archived", label: "Archived & blocked" },
];

const GROUPS = Object.entries(TAG_GROUP_LABELS);

/** Ceilings that keep audit queries and CSV exports responsive on large histories. */
const AUDIT_FETCH_LIMIT = 1000;
const AUDIT_EXPORT_LIMIT = 1000;
const AUDIT_PAGE_SIZES = [25, 50, 100];

function AdminTagsPage() {
  const { isAdmin, isOwner } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState<AdminTagFilter>("most_used");
  const [search, setSearch] = React.useState("");
  const [newTag, setNewTag] = React.useState({ name: "", group: "other" });
  const [aliasDraft, setAliasDraft] = React.useState<Record<string, string>>({});
  const [mergeDraft, setMergeDraft] = React.useState<Record<string, string>>({});
  const [historyFor, setHistoryFor] = React.useState<string | null>(null);
  const [auditFilters, setAuditFilters] = React.useState<{
    action: string;
    actor: string;
    from: string;
    to: string;
  }>({ action: "all", actor: "all", from: "", to: "" });
  const [auditPageSize, setAuditPageSize] = React.useState(25);
  const [auditPage, setAuditPage] = React.useState(0);

  const allowed = isAdmin || isOwner;

  const tags = useQuery({
    queryKey: ["admin-tags", filter, search],
    queryFn: () => fetchAdminTags(filter, search),
    enabled: allowed,
  });

  const aliases = useQuery({
    queryKey: ["admin-tag-aliases", (tags.data ?? []).map((tag) => tag.id).join(",")],
    queryFn: () => fetchTagAliases((tags.data ?? []).map((tag) => tag.id)),
    enabled: allowed && (tags.data ?? []).length > 0,
  });

  const canonicalOptions = useQuery({
    queryKey: ["tag-search", "admin-canonical"],
    queryFn: () => searchTags("", 50),
    enabled: allowed,
  });

  // Append-only history: who approved, rejected, merged, archived or blocked what.
  const auditQuery = React.useMemo(
    () => ({
      tagId: historyFor,
      actions: auditFilters.action === "all" ? [] : [auditFilters.action],
      actorId: auditFilters.actor === "all" ? null : auditFilters.actor,
      from: auditFilters.from ? new Date(auditFilters.from).toISOString() : null,
      // Date inputs are inclusive days, so extend the upper bound to end of day.
      to: auditFilters.to
        ? new Date(new Date(auditFilters.to).getTime() + 86_400_000).toISOString()
        : null,
      // One row over the export ceiling, so we can tell the admin when their
      // filters match more history than a single export can safely carry.
      limit: AUDIT_FETCH_LIMIT + 1,
    }),
    [historyFor, auditFilters],
  );

  const auditLog = useQuery({
    queryKey: ["tag-audit-log", auditQuery],
    queryFn: () => fetchTagAuditLog(auditQuery),
    enabled: allowed,
  });

  const auditActors = useQuery({
    queryKey: ["tag-audit-actors"],
    queryFn: fetchTagAuditActors,
    enabled: allowed,
  });

  const auditEntries = React.useMemo(
    () => (auditLog.data ?? []).slice(0, AUDIT_FETCH_LIMIT),
    [auditLog.data],
  );
  const auditTruncated = (auditLog.data ?? []).length > AUDIT_FETCH_LIMIT;
  const auditPageCount = Math.max(1, Math.ceil(auditEntries.length / auditPageSize));
  const currentAuditPage = Math.min(auditPage, auditPageCount - 1);
  const auditPageEntries = auditEntries.slice(
    currentAuditPage * auditPageSize,
    currentAuditPage * auditPageSize + auditPageSize,
  );

  // Reset to the first page whenever the filtered set changes.
  React.useEffect(() => {
    setAuditPage(0);
  }, [auditFilters, historyFor, auditPageSize]);

  function exportAuditCsv() {
    if (auditEntries.length === 0) {
      toast.error("Nothing to export for these filters.");
      return;
    }
    const entries = auditEntries.slice(0, AUDIT_EXPORT_LIMIT);
    const blob = new Blob([tagAuditToCsv(entries)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `quibblies-tag-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    if (auditTruncated || entries.length < auditEntries.length) {
      toast.warning(
        `Exported the most recent ${entries.length} entries — narrow the date range or filters to export the rest.`,
      );
    } else {
      toast.success(`Exported ${entries.length} audit entries.`);
    }
  }


  function refresh(tagId?: string) {
    void queryClient.invalidateQueries({ queryKey: ["tag-audit-log"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-tags"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-tag-aliases"] });
    void queryClient.invalidateQueries({ queryKey: ["tag-search"] });
    // Approvals, merges, archives and blocks change what a hashtag aggregates,
    // so drop the server-side trend cache and any hover panels already fetched.
    void invalidateTagTrendCacheFn({ data: { tagId: tagId ?? null } }).catch(() => {
      /* cache invalidation is best-effort; the TTL still expires on its own */
    });
    void queryClient.invalidateQueries({ queryKey: ["tag-trend-breakdown"] });
    void queryClient.invalidateQueries({ queryKey: ["trending-tags"] });
  }

  const createTag = useMutation({
    mutationFn: async () => {
      const name = normaliseTagQuery(newTag.name);
      if (name.length < 2) throw new Error("Tag names need at least 2 characters");
      const { data: slug } = await supabase.rpc("slugify_tag", { _text: name });
      const { error } = await supabase.from("tags").insert({
        name,
        slug: slug ?? name.toLowerCase(),
        status: "active",
        tag_group: newTag.group as "other",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setNewTag({ name: "", group: "other" });
      refresh();
      toast.success("Tag created");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateTag = useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Record<string, unknown>;
    }) => {
      const { error } = await supabase
        .from("tags")
        .update({ ...input.patch, updated_at: new Date().toISOString() })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: (_data, input) => {
      refresh(input.id);
      toast.success("Tag updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const addAlias = useMutation({
    mutationFn: async (input: { tagId: string; alias: string }) => {
      // Conflict detection lives in the database: already-assigned aliases,
      // clashes with an existing canonical hashtag and merged targets all come
      // back as readable errors.
      const alias = normaliseTagQuery(input.alias);
      if (alias.length < 2) throw new Error("Aliases need at least 2 characters");
      await addTagAlias(input.tagId, alias);
    },
    onSuccess: (_data, input) => {
      setAliasDraft((prev) => ({ ...prev, [input.tagId]: "" }));
      refresh(input.tagId);
      toast.success("Alias added — searches for it now resolve to the canonical tag.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const merge = useMutation({
    mutationFn: async (input: { sourceId: string; targetId: string }) => {
      const { error } = await supabase.rpc("merge_tags", {
        _source_id: input.sourceId,
        _target_id: input.targetId,
      });
      if (error) throw error;
    },
    onSuccess: (_data, input) => {
      refresh(input.sourceId);
      refresh(input.targetId);
      toast.success("Tags merged — the duplicate now redirects to the canonical hashtag.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          icon={Hash}
          title="Administrators only"
          description="Tag management is restricted to administrators and the owner."
        />
      </div>
    );
  }

  const rows = tags.data ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <header>
        <h1 className="font-display text-4xl text-foreground">Tags</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The hashtag registry. Writers can only apply active tags; requests stay private until you
          approve them.
        </p>
      </header>

      <TagTrendMetricsCard />

      <section className="mt-8 rounded-3xl border border-border/70 bg-surface/40 p-4">

        <Label htmlFor="new-tag">Create tag</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          <Input
            id="new-tag"
            value={newTag.name}
            placeholder="NewTag"
            className="max-w-xs"
            onChange={(event) => setNewTag((prev) => ({ ...prev, name: event.target.value }))}
          />
          <Select
            value={newTag.group}
            onValueChange={(value) => setNewTag((prev) => ({ ...prev, group: value }))}
          >
            <SelectTrigger className="max-w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUPS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="soft" disabled={createTag.isPending} onClick={() => createTag.mutate()}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Create
          </Button>
        </div>
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            className={cn(
              "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
              filter === option.value
                ? "border-primary/60 bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
        <Input
          value={search}
          placeholder="Search tags"
          className="ml-auto max-w-xs"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="mt-6 space-y-3">
        {tags.isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))
        ) : rows.length === 0 ? (
          <EmptyState icon={Hash} title="No tags here" description="Nothing matches this view." />
        ) : (
          rows.map((tag) => {
            const tagAliases = (aliases.data ?? []).filter((alias) => alias.tag_id === tag.id);
            return (
              <article
                key={tag.id}
                className="rounded-2xl border border-border/70 bg-surface/40 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <TagPill tag={tag} active />
                    <span className="text-xs text-muted-foreground">
                      {TAG_GROUP_LABELS[tag.tag_group ?? "other"]} · {tag.usage_count} uses ·{" "}
                      {tag.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {tag.status === "pending" ? (
                      <>
                        <Button
                          size="sm"
                          variant="hero"
                          onClick={() =>
                            updateTag.mutate({
                              id: tag.id,
                              patch: { status: "active", reviewed_at: new Date().toISOString() },
                            })
                          }
                        >
                          <Check className="h-4 w-4" aria-hidden="true" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="soft"
                          onClick={() =>
                            updateTag.mutate({
                              id: tag.id,
                              patch: { status: "blocked", reviewed_at: new Date().toISOString() },
                            })
                          }
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                          Reject
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="soft"
                          onClick={() =>
                            updateTag.mutate({
                              id: tag.id,
                              patch: { status: tag.status === "archived" ? "active" : "archived" },
                            })
                          }
                        >
                          {tag.status === "archived" ? "Restore" : "Archive"}
                        </Button>
                        <Button
                          size="sm"
                          variant="soft"
                          onClick={() => updateTag.mutate({ id: tag.id, patch: { status: "blocked" } })}
                        >
                          Block
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Display name</Label>
                    <div className="flex gap-2">
                      <Input
                        defaultValue={tag.name}
                        onBlur={(event) => {
                          const name = normaliseTagQuery(event.target.value);
                          if (name && name !== tag.name) {
                            updateTag.mutate({ id: tag.id, patch: { name } });
                          }
                        }}
                      />
                      <Select
                        value={tag.tag_group ?? "other"}
                        onValueChange={(value) =>
                          updateTag.mutate({ id: tag.id, patch: { tag_group: value } })
                        }
                      >
                        <SelectTrigger className="max-w-[190px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GROUPS.map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Aliases</Label>
                    <div className="flex gap-2">
                      <Input
                        value={aliasDraft[tag.id] ?? ""}
                        placeholder="Alternative spelling"
                        onChange={(event) =>
                          setAliasDraft((prev) => ({ ...prev, [tag.id]: event.target.value }))
                        }
                      />
                      <Button
                        size="sm"
                        variant="soft"
                        onClick={() =>
                          addAlias.mutate({ tagId: tag.id, alias: aliasDraft[tag.id] ?? "" })
                        }
                      >
                        Add
                      </Button>
                    </div>
                    {tagAliases.length > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {tagAliases.map((alias) => alias.alias).join(", ")}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Label className="text-xs">Merge into</Label>
                  <Select
                    value={mergeDraft[tag.id] ?? ""}
                    onValueChange={(value) =>
                      setMergeDraft((prev) => ({ ...prev, [tag.id]: value }))
                    }
                  >
                    <SelectTrigger className="max-w-[240px]">
                      <SelectValue placeholder="Canonical tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {(canonicalOptions.data ?? [])
                        .filter((option) => option.id !== tag.id)
                        .map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            #{option.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="soft"
                    disabled={!mergeDraft[tag.id] || merge.isPending}
                    onClick={() =>
                      merge.mutate({ sourceId: tag.id, targetId: mergeDraft[tag.id]! })
                    }
                  >
                    <Merge className="h-4 w-4" aria-hidden="true" />
                    Merge
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => setHistoryFor(historyFor === tag.id ? null : tag.id)}
                  >
                    <History className="h-4 w-4" aria-hidden="true" />
                    {historyFor === tag.id ? "Hide history" : "History"}
                  </Button>
                </div>
              </article>
            );
          })
        )}
      </div>

      <section className="mt-12">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-2xl text-foreground">Tag audit log</h2>
          <div className="flex flex-wrap gap-2">
            {historyFor ? (
              <Button size="sm" variant="soft" onClick={() => setHistoryFor(null)}>
                Show all tags
              </Button>
            ) : null}
            <Button size="sm" variant="soft" onClick={exportAuditCsv}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Export CSV
            </Button>
          </div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Append-only. Every approval, rejection, merge, rename, archive, block and alias change is
          recorded with the staff member who made it.
        </p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label htmlFor="audit-action" className="text-xs text-muted-foreground">
              Action
            </Label>
            <Select
              value={auditFilters.action}
              onValueChange={(value) => setAuditFilters((prev) => ({ ...prev, action: value }))}
            >
              <SelectTrigger id="audit-action" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {Object.entries(TAG_AUDIT_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="audit-actor" className="text-xs text-muted-foreground">
              Staff member
            </Label>
            <Select
              value={auditFilters.actor}
              onValueChange={(value) => setAuditFilters((prev) => ({ ...prev, actor: value }))}
            >
              <SelectTrigger id="audit-actor" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {(auditActors.data ?? []).map((actor) => (
                  <SelectItem key={actor.actor_id} value={actor.actor_id}>
                    {actor.display_name ?? actor.username ?? "Unknown"} ({actor.entries})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="audit-from" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="audit-from"
              type="date"
              className="mt-1"
              value={auditFilters.from}
              onChange={(event) =>
                setAuditFilters((prev) => ({ ...prev, from: event.target.value }))
              }
            />
          </div>

          <div>
            <Label htmlFor="audit-to" className="text-xs text-muted-foreground">
              To
            </Label>
            <Input
              id="audit-to"
              type="date"
              className="mt-1"
              value={auditFilters.to}
              onChange={(event) => setAuditFilters((prev) => ({ ...prev, to: event.target.value }))}
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>
            {auditLog.isLoading
              ? "Loading…"
              : `${auditEntries.length}${auditTruncated ? "+" : ""} entries`}
            {historyFor ? " for the selected hashtag" : ""}
          </span>
          <label className="flex items-center gap-1.5">
            <span>Per page</span>
            <select
              className="rounded-md border border-border/70 bg-surface/60 px-1.5 py-0.5 text-xs text-foreground"
              value={auditPageSize}
              onChange={(event) => setAuditPageSize(Number(event.target.value))}
              aria-label="Audit entries per page"
            >
              {AUDIT_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          {auditFilters.action !== "all" ||
          auditFilters.actor !== "all" ||
          auditFilters.from ||
          auditFilters.to ||
          historyFor ? (
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground"
              onClick={() => {
                setHistoryFor(null);
                setAuditFilters({ action: "all", actor: "all", from: "", to: "" });
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>

        {auditTruncated ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Showing the most recent {AUDIT_FETCH_LIMIT} entries. CSV exports are capped at{" "}
            {AUDIT_EXPORT_LIMIT} rows — narrow the date range, tag or action to export older
            history.
          </p>
        ) : null}

        <div className="mt-3 divide-y divide-border/60 rounded-2xl border border-border/70 bg-surface/40">
          {auditLog.isLoading ? (
            <div className="p-4">
              <Skeleton className="h-16 rounded-xl" />
            </div>
          ) : auditEntries.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No tag changes match these filters.
            </p>
          ) : (
            auditPageEntries.map((entry) => {
              const meta = (entry.metadata ?? {}) as Record<string, unknown>;
              const name =
                entry.tag_name ?? (typeof meta["name"] === "string" ? (meta["name"] as string) : null);
              const alias = typeof meta["alias"] === "string" ? (meta["alias"] as string) : null;
              return (
                <div key={entry.id} className="flex flex-wrap items-baseline gap-2 p-3 text-sm">
                  <span className="font-medium text-foreground">
                    {TAG_AUDIT_LABELS[entry.action] ?? entry.action}
                  </span>
                  {name ? <span className="text-muted-foreground">#{name}</span> : null}
                  {alias ? <span className="text-muted-foreground">alias “{alias}”</span> : null}
                  <span className="text-xs text-muted-foreground">
                    by{" "}
                    {entry.actor_display_name
                      ? `${entry.actor_display_name} (@${entry.actor_username})`
                      : "system"}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {auditEntries.length > auditPageSize ? (
          <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              Page {currentAuditPage + 1} of {auditPageCount}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="soft"
                disabled={currentAuditPage === 0}
                onClick={() => setAuditPage((page) => Math.max(0, page - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="soft"
                disabled={currentAuditPage >= auditPageCount - 1}
                onClick={() => setAuditPage((page) => Math.min(auditPageCount - 1, page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}

      </section>
    </div>
  );
}
